// @flow

import React from 'react';
import styled, { css } from 'styled-components';
import { prop } from 'styled-tools';
import { palette, size } from 'styled-theme';

type Props = {
  display?: 'inline-block' | 'block',
  disabled?: boolean,
  type?: 'button' | 'submit' | 'reset',
  buttonStyle?: 'bordered' | 'link',
  fontSize?: string,
  height?: string,
  lineHeight?: string,
  backgroundColor?: string,
  whiteSpace?: string,
  padding?: string,
  children?: any,
  component?: any,
  onClick?: Function,
};

const StyledButton = styled.button`
  display: ${(props: Props) => props.display};
  height: ${prop('height', '50px')};
  margin: 0;
  padding: ${prop('padding', '0 30px')};
  border: none;
  cursor: pointer;
  box-sizing: border-box;
  color: #fff;
  border-radius: ${size('borderRadius')};
  transition: opacity 0.2s;
  text-align: center;
  user-select: none;
  font-size: ${prop('fontSize', '18px')};
  line-height: ${prop('lineHeight', '23px')};
  font-weight: normal;
  background-color: ${prop('backgroundColor', palette('success', 0))};
  white-space: ${prop('whiteSpace', 'inherit')};

  ${(props: Props) => css`
    ${props.display === 'block' &&
      css`
        display: block;
        width: 100%;
      `};

    ${props.buttonStyle === 'bordered' &&
      css`
        background-color: transparent;
        border: solid 1px ${palette('borders', 0)};
        font-size: ${prop('fontSize', '18px')};
        font-weight: normal;
        line-height: 22px;
        color: ${palette('text', 0)};
      `};

    ${props.buttonStyle === 'link' &&
      css`
        background-color: transparent;
        border: none;
        font-size: ${prop('fontSize', '18px')};
        font-weight: normal;
        line-height: 22px;
        color: ${palette('links', 0)};
        height: auto;
        padding: ${prop('padding', '5px')};
      `};

    ${props.disabled &&
      css`
        pointer-events: none;
        opacity: 0.5;
      `};

    transition: all 0.2s;
  `};

  &:hover {
    filter: brightness(1.1);
  }

  &:focus {
    outline: none;
  }

  &:active {
    filter: brightness(1.2);
  }
`;

const Button = ({
  type = 'button',
  display = 'inline-block',
  disabled = false,
  component = 'button',
  children,
  ...rest
}: Props) => {
  const Component = StyledButton.withComponent(component);

  return (
    <Component type={type} display={display} disabled={disabled} {...rest}>
      {children}
    </Component>
  );
};

export const BorderedButton = (props: Props) => (
  <Button buttonStyle="bordered" {...props} />
);

export const LinkButton = (props: Props) => (
  <Button buttonStyle="link" {...props} />
);

export default Button;
